import React, { PureComponent } from 'react';
import styled from 'styled-components';
import { Grid, List, ListItem, ListItemText, Typography, Box, IconButton } from '@mic3/platform-ui';
import TextareaDialog from 'app/containers/Designer/Form/components/TextareaDialog';
import Help from 'app/utils/designer/form/settings/common/Help';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';

import { saveFiltersPreferences, saveColumnPreferences } from 'store/actions/admin/usersActions';
import { loadRelationDefinitionTypeahead } from 'store/actions/entities/relationshipsActions';
import { saveComponentState } from 'store/actions/component/componentActions';
import { bind, memoize, debounce } from 'app/utils/decorators/decoratorUtils';
import DraggableElement from 'app/components/molecules/Dnd/DraggableElement';
import RowTarget from 'app/components/molecules/Dnd/RowTarget';
import Icon from 'app/components/atoms/Icon/Icon';
import { showToastr } from 'store/actions/app/appActions';
import { isEmpty } from 'app/utils/utils';
import { setSubTitle, closeSidebar, setTitle } from 'store/actions/sidebar/sidebarActions';
import { deepCloneArray } from 'app/utils/array/array-utils';
import DotMenu from 'app/components/molecules/DotMenu/DotMenu';
import RelationAttributeModal from './RelationAttributeModal';
import AttributeModal from './AttributeModal';

const GridStyled = styled(Grid)`
    display: flex;
    align-items: center;
    padding: 0px 8px !important;
    margin-bottom: 16px;
`;

const StyledIconButton = styled(IconButton)`
    vertical-align: middle;
    cursor: point;
    color: ${({ theme }) => theme.material.colors.text.secondary} !important;
`;

const ListItemTextStyled = styled(ListItemText)`
    & .MuiTypography-root {
        overflow: hidden;
        text-overflow: ellipsis;
        width: 23rem;
        white-space: nowrap;
        text-align: left;
    }
`;

const StyledTypography = styled(Typography)`
    padding: 12px 0px 16px 24px;
    font-family: Roboto !important;
    font-size: 12px !important;
    font-weight: 600 !important;
    line-height: 16px !important;
    text-align: left;
    color: ${({ theme }) => theme.material.colors.text.secondary};
`;

const StyledIcon = styled(Icon)`
    padding-right: 1rem;
    vertical-align: middle;
    height: 36px;
    cursor: grab;
    margin-right: 8px;
`;

const DraggableElementStyled = styled(DraggableElement)`
    cursor: pointer !important;
    width: 100%;
    div {
        display: flex;
        align-items: center;
    }
`;

const ColumnListWrapper = styled.div`
    width: 100%;
    overflow: hidden;
`;

const styles = (theme) => ({
    applyButton: {
        marginRight: '8px',
        marginTop: '6px',
        color: ' #7391D0 !important',
    },
    menuIcon: {
        marginRight: '1rem',
        color: 'rgba(255, 255, 255, 0.6)',
    },
    targetRow: {
        textAlign: 'start !important',
    },
});
const StyledBox = styled(Box)`
    background-color: ${({ theme }) => theme.material.colors.background.fields};
    border-radius: 4px 4px 0px 0px;
`;

const mapLabelByType = {
    geotagPosition: 'Geotag Position',
    geotagLocation: 'Geotag Location',
};

const isVisibleCodeExample = `
// Example:

/**
 * The function will handle the component visibility and it must return a
 * - boolean value
 * - name of task priority for coloring labels ('highest', 'high', 'medium', 'low', 'lowest')
 * 
 * If the returned value is true the component will be visible.
 * If the returned value is priority color the component will be visible and colored with the corresponding color. 
 *
 * @param {Object} data - the data accessible from attribute
 * @param {Object} context - the global context
 * @returns {boolean|string} - boolean for visibility or priority string for colored visibility
 */
(data, context) => !data.name

`;



class AddEntitityAttributes extends PureComponent {
    newColumnRef = React.createRef();

    defaultEntityFields = {
        label: 'Entity',
        value: 'entity',
        checked: false,
        children: [
            { label: 'Entity name', value: 'name', fieldType: 'text' },
            { label: 'Active', value: 'active', fieldType: 'boolean' },
            { label: 'Description', value: 'description', fieldType: 'textarea' },
            { label: 'Created by', value: 'createdBy.username', fieldType: 'userTypeahead' },
            { label: 'Created date', value: 'createdDate', fieldType: 'date' },
            { label: 'Modified by', value: 'modifiedBy.username', fieldType: 'userTypeahead' },
            { label: 'Modified date', value: 'modifiedDate', fieldType: 'date' },
        ].map((item) => ({ checked: false, ...item })),
    };
    relatedEntityFields = {
        label: 'Entity',
        value: 'entity',
        checked: false,
        children: [{ label: 'Related Entity name', value: 'attributes.relatedEntity.name', fieldType: 'text' }].map((item) => ({ checked: false, ...item })),
    };

    constructor(props) {
        super(props);
        const { primaryClasses, entityType, value, relatedEntityDefinition } = props;
        const selectedPrimaryClass = primaryClasses?.find((cls) => cls?.uri === entityType);

        this.state = {
            isAddAttributeOpen: false,
            isAddRelationAttributeOpen: false,
            value: value?.map((val, i) => ({ ...val, order: i })) || [],
            entityTreeValue: this.normalizeDefintionsToCheckboxes([], selectedPrimaryClass, value, null),
            relatedEntityDefinition: relatedEntityDefinition,
            relationDefinitionTreeValue: this.normalizeDefintionsToCheckboxes([], null, value, relatedEntityDefinition || null),
        };
    }

    componentDidMount() {
        this.loadRelationDefinition()
    }

    componentDidUpdate(prevProps, prevState) {
        const { entityType, primaryClasses, value, parentName } = this.props;
        const { relatedEntityDefinition } = this.state
        const nextState = {};

        if (prevProps.value !== value) {
            nextState.value = (value?.attributesList || value)?.map((val, i) => ({ ...val, order: i })) || [];

            const selectedPrimaryClass = primaryClasses?.find((cls) => cls?.uri === entityType);
            nextState.entityTreeValue = this.normalizeDefintionsToCheckboxes([], selectedPrimaryClass, nextState.value, null);
            nextState.relationDefinitionTreeValue = this.normalizeDefintionsToCheckboxes([], null, nextState.value, relatedEntityDefinition || null);
        }
        if (prevProps.entityType !== entityType) {
            const selectedPrimaryClass = primaryClasses?.find((cls) => cls?.uri === entityType);
            nextState.entityTreeValue = this.normalizeDefintionsToCheckboxes([], selectedPrimaryClass, [], null);
            if (prevProps?.parentName === parentName) this.updateEmptyProps();
        }
        if (prevState.relatedEntityDefinition !== relatedEntityDefinition) {
            nextState.relationDefinitionTreeValue = this.normalizeDefintionsToCheckboxes([], null, value, relatedEntityDefinition || null);
        }

        if (prevState?.relatedEntityDefinition?.classes !== relatedEntityDefinition?.classes) {

            let newValue = (value?.attributesList || value)?.map((val, i) => ({ ...val, order: i })) || [];
            newValue = this.filterAttributesByClasses(newValue, relatedEntityDefinition?.classes);
            nextState.value = newValue;
        }

        if (!isEmpty(nextState)) {
            this.setState(nextState);
        }
    }

    @bind
    collectFieldNames(fields, acc = new Set()) {
        fields.forEach(field => {
            const name = field?.properties?.name;
            if (name) {
                acc.add(name);
            }
            if (field.children?.length) {
                this.collectFieldNames(field.children, acc);
            }
        });
        return acc;
    }

    @bind
    filterAttributesByClasses(attributesList, classes) {
        if (!classes?.length) return attributesList;

        const allowed = new Set();
        classes.forEach(cls => {
            this.collectFieldNames(cls.formDefinition?.fields || [], allowed);
        });

        return attributesList.filter(attr => {
            const val = attr.value.startsWith('attributes.')
                ? attr.value.replace(/^attributes\./, '')
                : attr.value;
            return allowed.has(val);
        });
    };

    @bind
    loadRelationDefinition() {
        const { relatedEntityDefinition, loadRelationDefinitionTypeahead } = this.props;
        if (relatedEntityDefinition?.id) {
            loadRelationDefinitionTypeahead({
                filterBy: [{ field: 'id', op: '=', value: relatedEntityDefinition?.id }]
            }).then(data => {
                const relatedEntityDefinition = data[0]
                this.setState({ relatedEntityDefinition })
            });
        }
    }

    @bind
    @debounce()
    updateEmptyProps() {
        this.props.onChange({
            target: {
                name: this.props.name,
                value: {
                    attributesList: [],
                    relatedEntity: null,
                    relatedEntityDefinition: null,
                },
            },
        });
    }

    @bind
    toggleNewAttributeDialog() {
        const { entityType, primaryClasses, value } = this.props;

        if (this.state.isAddAttributeOpen) {
            const selectedPrimaryClass = primaryClasses?.find((cls) => cls?.uri === entityType);
            this.setState({ entityTreeValue: this.normalizeDefintionsToCheckboxes([], selectedPrimaryClass, value, null)})
        }

        this.setState((state) => ({ isAddAttributeOpen: !state.isAddAttributeOpen }));
    }
    @bind
    toggleNewRelationAttributeDialog() {
        const { value } = this.props;
        const { relatedEntityDefinition } = this.state
        if(this.state.isAddRelationAttributeOpen){
            this.setState({ relationDefinitionTreeValue: this.normalizeDefintionsToCheckboxes([], null, value, relatedEntityDefinition || null)})
        }
        this.setState((state) => ({ isAddRelationAttributeOpen: !state.isAddRelationAttributeOpen }));
    }

    @bind
    onDrop(item, index) {
        const listItem = { ...item };
        listItem.order = index;
        this.move(item.order, listItem, index);
    }

    @bind
    move(prevOrder, elementDragged, index) {
        const { value: attributesList } = this.state;
        const draggableColumns = deepCloneArray(attributesList);
        draggableColumns.splice(prevOrder, 1);
        draggableColumns.splice(index, 0, elementDragged);
        draggableColumns.forEach((item, idx) => {
            item.order = idx;
        });

        this.setState({ value: draggableColumns });
        this.props.onChange({
            target: {
                name: this.props.name,
                value: {
                    attributesList: draggableColumns?.map((val, i) => ({ ...val, order: i })),
                    relatedEntity: this.props.relatedEntity,
                    relatedEntityDefinition: this.state.relatedEntityDefinition,
                },
            },
        });
    }

    @bind
    handleAttributeApply(entityTreeValue, attributesList) {
        this.setState({ entityTreeValue, value: attributesList }, () =>
            this.props.onChange({
                target: {
                    name: this.props.name,
                    value: {
                        attributesList,
                        relatedEntity: this.props.relatedEntity,
                        relatedEntityDefinition: this.state.relatedEntityDefinition,
                    },
                },
            })
        );
    }

    @bind
    handleRelationAttributeApply(relationDefinitionTreeValue, attributesList, relatedEntity, relatedEntityDefinition) {
        this.setState({ relationDefinitionTreeValue, value: attributesList }, () =>
            this.props.onChange({
                target: {
                    name: this.props.name,
                    value: {
                        attributesList,
                        relatedEntity,
                        relatedEntityDefinition,
                    },
                },
            })
        );
    }

    @bind
    normalizeDefintionsToCheckboxes(treeValue, primaryClass, value = [], relatedEntityDefinition) {
        const parseDefinitions = (defs, parentPath) =>
            defs
                .map((field, index) => {
                    const name = field?.properties?.name;
                    const path = parentPath && name ? `${parentPath}.${name}` : name || parentPath;
                    const isGroup = ['group', 'groupRepeat', 'panel'].includes(field.type);
                    if ((!name && !isGroup) || field.properties.extraType === 'password' 
                    || field.type === 'json'
                    || field.type === 'personTypeahead'
                    || (field.type === 'textarea' && field?.properties?.parseAs === 'JSON')
                    ) {
                        return null;
                    }
                    const stateItem = treeValue?.find((tr) => tr.value === path);
                    if (stateItem) {
                        return stateItem;
                    }
                    if (isGroup) {
                        if (field?.type === 'group' || field?.type === 'groupRepeat') {
                            // const flatChildren = flattenChildren(field.children || []);
                            // const children = parseDefinitions(flatChildren || [], path, true).filter(Boolean);
                            // return {
                            //     label: field?.properties?.label || field?.properties?.placeholder || field?.properties?.name,
                            //     value: path,
                            //     checked: !!value.find((val) => val.value === path),
                            //     disabled: false,
                            //     children,
                            // };
                            return;
                        }
                        return parseDefinitions(field.children || [], path).filter(Boolean);
                    }

                    return {
                        label: field?.properties?.label || field?.properties?.placeholder || mapLabelByType[field.type],
                        value: path,
                        fieldType: field.type,
                        checked: !!value.find((val) => val.value === path),
                        disabled: false,
                    };
                })
                .filter(Boolean)
                .flat(Infinity)
                .filter((d) => d.value && d.label);

        let entityFields = treeValue?.find((tr) => tr.value === 'entity') || null;
        if (!entityFields) {
            const children = this.defaultEntityFields.children.map((def) => ({ ...def, checked: !!value.find((val) => val.value === def.value) }));
            entityFields = {
                ...this.defaultEntityFields,
                checked:  !children.find((def) => !def.checked),
                children
            };
        }

        if (relatedEntityDefinition) {
            this.relatedEntityFields.children.forEach((def) => {
                const checked = value.find((val) => val.value === def.value);
                def.checked = !!checked;
            });
            this.relatedEntityFields.checked = !this.relatedEntityFields.children.find((def) => !def.checked);
        }
        return [
            primaryClass && entityFields,
            ...[primaryClass].filter(Boolean).map((clss) => {
                const { formDefinition, name, uri } = clss;
                const stateItem = treeValue?.find((tr) => tr.value === uri);
                if (stateItem) {
                    return stateItem;
                }
                const children = parseDefinitions(formDefinition?.fields || [], 'primary');
                return {
                    label: name,
                    value: uri,
                    checked: stateItem?.checked || !children.find((def) => !def.checked),
                    children,
                };
            }),
            ...[
                relatedEntityDefinition && this.relatedEntityFields,
                ...(relatedEntityDefinition?.classes || []).filter(Boolean).map((clss) => {
                    const { formDefinition, name, uri } = clss;
                    const stateItem = treeValue?.find((tr) => tr.value === `relations.entity.${uri}`);
                    const stateAtr = treeValue?.find((tr) => tr.value === `entity`);
                    if (stateItem || stateAtr) {
                        return stateItem || stateAtr;
                    }
                    const children = parseDefinitions(formDefinition?.fields || [], 'attributes')
                    return {
                        label: name,
                        value: `relations.entity.${uri}`,
                        checked: stateItem?.checked || !children.find((def) => !def.checked),
                        children: children,
                    };
                }),
            ],
        ].filter(Boolean);
    }

    @bind
    updateAttributesOrder(value, nodes) {
        let order = -1;
        if (nodes[0].checked) {
            const filteredNodes = nodes.filter((n) => !value.find((v) => v.value === n.value));
            return [...value, ...filteredNodes].map((def) => {
                order = order + 1;
                return { ...def, order };
            });
        }
        return [...value]
            .filter((val) => !nodes.find((node) => node.value === val.value))
            .map((def) => {
                order = order + 1;
                return { ...def, order };
            });
    }

    @bind
    removeAttribute(attributesList, def) {
        this.props.onChange({
            target: {
                name: this.props.name,
                value: {
                    attributesList: attributesList.filter((attr) => attr.value !== def.value),
                    relatedEntity: this.props.relatedEntity,
                    relatedEntityDefinition: this.state.relatedEntityDefinition,
                },
            },
        });
    }
    @bind
    handleIsVisible(e, attributesList, def ) {
        const normalizedAttributesList = attributesList.map((attr) => {
            if (attr.value === def.value) {
                return { ...attr, isVisible: e.target.value };
            }
            return attr;
        })

        this.props.onChange({
            target: {
                name: this.props.name,
                value: {
                    attributesList: normalizedAttributesList,
                    relatedEntity: this.props.relatedEntity,
                    relatedEntityDefinition: this.state.relatedEntityDefinition,
                },
            },
        })
    }


    @bind
    @memoize()
    buildColumns(attributesList) {
        return attributesList?.map((def, i) => (
            <ListItem key={def.order} id={def.order} dense button>
                <DraggableElementStyled key={i} index={i} element={def}>
                    <RowTarget index={i} onDrop={this.onDrop} element={def}>
                        <StyledIcon name="drag-vertical" />
                        <ListItemTextStyled id={def.value} primary={def.label} />
                        <TextareaDialog 
                            parseAs= {'javascript'}
                            label= {'isVisible'}
                            name ={'properties.isVisible'}
                            help={<Help message="This function defines the visibility of the component." />}
                            title={'Visibility'}
                            template={isVisibleCodeExample}
                            modal
                            useCodeEditor
                            iconButtonName={'pencil'}
                            onChange={(e)=>this.handleIsVisible(e, attributesList, def)}
                            value={def?.isVisible}
                        />
                        <StyledIconButton onClick={() => this.removeAttribute(attributesList, def)}>
                            <Icon name="close" />
                        </StyledIconButton>

                    </RowTarget>
                </DraggableElementStyled>
            </ListItem>
        ));
    }

    render() {
        const { isAddRelationAttributeOpen, isAddAttributeOpen, entityTreeValue, value: attributesList,
            relationDefinitionTreeValue, relatedEntityDefinition } = this.state;
        const { relatedEntity, entityType } = this.props;
        return (
            <ColumnListWrapper>
                <GridStyled container wrap="nowrap">
                    <DotMenu
                        buttonProps={{
                            disabled: !entityType,
                            label: '+ ADD NEW',
                        }}
                        items={[
                            {
                                name: 'Attribute',
                                onItemClick: this.toggleNewAttributeDialog,
                            },
                            {
                                name: 'Relationship attributes',
                                onItemClick: this.toggleNewRelationAttributeDialog,
                            },
                        ]}
                    />
                </GridStyled>
                <StyledBox>
                    {attributesList.length ? (
                        <List>
                            <StyledTypography> Drag to re-order </StyledTypography>
                            {this.buildColumns(attributesList)}
                        </List>
                    ) : null}
                    {isAddAttributeOpen && (
                        <AttributeModal
                            open={isAddAttributeOpen}
                            onClose={this.toggleNewAttributeDialog}
                            onApply={this.handleAttributeApply}
                            entityTreeValue={entityTreeValue}
                            value={attributesList}
                            updateAttributesOrder={this.updateAttributesOrder}
                        />
                    )}
                    {isAddRelationAttributeOpen && (
                        <RelationAttributeModal
                            open={isAddRelationAttributeOpen}
                            onClose={this.toggleNewRelationAttributeDialog}
                            onApply={this.handleRelationAttributeApply}
                            relatedEntity={relatedEntity}
                            relatedEntityDefinition={relatedEntityDefinition}
                            relationDefinitionTreeValue={relationDefinitionTreeValue}
                            updateAttributesOrder={this.updateAttributesOrder}
                            value={attributesList}
                            normalizeDefintionsToCheckboxes={this.normalizeDefintionsToCheckboxes}
                            entityType={entityType}
                        />
                    )}
                </StyledBox>
            </ColumnListWrapper>
        );
    }
}

export default connect(
    (state, props) => ({
        primaryClasses: state.app.allPrimaryClasses.records || [],
    }),
    { saveComponentState, showToastr, setSubTitle, closeSidebar, setTitle, saveFiltersPreferences, saveColumnPreferences, loadRelationDefinitionTypeahead }
)(withStyles(styles)(AddEntitityAttributes));
